/**
 * Size Guide drawer — right-side slide-in on the product page.
 * Ported from the Turn Paris theme.
 *
 * The table is rendered server-side in decimal inches (see snippets/size-guide-drawer.liquid).
 * Both display units are derived here from the `data-in` attribute on each cell:
 * inches are shown as eighths ("25¾"), centimetres to one decimal. Only one unit
 * is ever stored, so the IN/CM toggle can never disagree with the data.
 */
class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();

    this.inner = this.querySelector(".size-guide-drawer__inner");

    this.addEventListener("keyup", (event) => {
      if (event.code === "Escape") this.close();
    });

    // Close on any click that lands outside the panel. Checking against the
    // panel (rather than listening on the overlay) keeps this working no matter
    // how the overlay itself is painted.
    this.addEventListener("click", (event) => {
      if (this.inner && !this.inner.contains(event.target)) this.close();
    });

    this.querySelector("[data-size-guide-close]")?.addEventListener(
      "click",
      this.close.bind(this)
    );

    this.querySelectorAll("[data-unit]").forEach((button) => {
      button.addEventListener("click", () => this.setUnit(button.dataset.unit));
    });

    // Format the server-rendered decimals into fractions before first paint.
    this.setUnit("in");
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    // Matches the cart drawer: a tick's delay so the transition actually runs.
    setTimeout(() => {
      this.classList.add("active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        if (typeof trapFocus === "function") {
          trapFocus(this.inner, this.querySelector("[data-size-guide-close]"));
        } else {
          this.inner?.focus();
        }
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove("active");

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus();
    }
  }

  setUnit(unit) {
    const toCentimetres = unit === "cm";

    this.querySelectorAll("[data-unit]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.unit === unit);
    });

    this.querySelectorAll("[data-in]").forEach((cell) => {
      const inches = parseFloat(cell.dataset.in);
      if (Number.isNaN(inches)) return;
      cell.textContent = toCentimetres
        ? (inches * 2.54).toFixed(1)
        : SizeGuideDrawer.formatInches(inches);
    });
  }

  // 25.75 -> "25¾", 33 -> "33". Measurements are graded in eighths.
  static formatInches(value) {
    const eighths = Math.round(value * 8);
    const whole = Math.floor(eighths / 8);
    const remainder = eighths % 8;
    const glyphs = { 1: "⅛", 2: "¼", 3: "⅜", 4: "½", 5: "⅝", 6: "¾", 7: "⅞" };
    if (remainder === 0) return String(whole);
    return whole === 0 ? glyphs[remainder] : `${whole}${glyphs[remainder]}`;
  }
}

customElements.define("size-guide-drawer", SizeGuideDrawer);

// Any [data-size-guide-open] trigger opens the drawer. Delegated so triggers
// that get re-rendered by a variant change keep working.
document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-size-guide-open]");
  if (!trigger) return;

  event.preventDefault();
  document.querySelector("size-guide-drawer")?.open(trigger);
});
